class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this))
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden')
    } else if (this.input.value.length === 0  && !resetIsHidden) {
      this.resetButton.classList.add('hidden')
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  normalizeVendorKey(value) {
    return this.decodeSearchDisplayValue(value)
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase()
      .replace(/\s*&\s*/g, '&');
  }

  addVendorMapEntry(vendorMap, key, vendor) {
    const normalizedKey = this.normalizeVendorKey(key);

    if (!normalizedKey || !vendor) return;

    vendorMap[normalizedKey] = vendor;
    vendorMap[normalizedKey.replace(/\s+/g, '')] = vendor;
  }

  getVendorMap() {
    const vendorMap = {};
    const configuredVendors = Array.isArray(window.BestTuningSearchVendors) ? window.BestTuningSearchVendors : [];
    const defaultAliases = {
      'ap': 'AP Sportfahrwerke',
      'bastuck': 'Bastuck',
      'bullx': 'Bull-X',
      'bull x': 'Bull-X',
      'ego-x': 'EGO-X',
      'ego x': 'EGO-X',
      'egox': 'EGO-X',
      'h&r': 'H&R',
      'kw': 'KW Automotive',
      'maxton': 'Maxton Design',
      'maxton design': 'Maxton Design',
      'maxtondesign': 'Maxton Design',
      'milltek sport': 'Milltek',
      'millteksport': 'Milltek',
      'mts': 'MTS Technik',
      'mts technik': 'MTS Technik',
      'mtstechnik': 'MTS Technik'
    };
    const configuredAliases = window.BestTuningSearchVendorAliases && typeof window.BestTuningSearchVendorAliases === 'object'
      ? window.BestTuningSearchVendorAliases
      : {};

    configuredVendors.forEach((vendor) => {
      if (typeof vendor !== 'string') return;

      const cleanVendor = vendor.trim();
      this.addVendorMapEntry(vendorMap, cleanVendor, cleanVendor);
    });

    [defaultAliases, configuredAliases].forEach((aliasMap) => {
      Object.keys(aliasMap).forEach((alias) => {
        const vendor = typeof aliasMap[alias] === 'string' ? aliasMap[alias].trim() : '';
        this.addVendorMapEntry(vendorMap, alias, vendor);
      });
    });

    return vendorMap;
  }

  getExactVendorSearchTerm(value) {
    const normalizedKey = this.normalizeVendorKey(value);
    const vendorMap = this.getVendorMap();

    return vendorMap[normalizedKey] || vendorMap[normalizedKey.replace(/\s+/g, '')] || '';
  }

  normalizeBrandSearchTerm(value) {
    const vendor = this.getExactVendorSearchTerm(value);

    return vendor ? `vendor:"${this.escapeSearchValue(vendor)}"` : value;
  }

  decodeSearchDisplayValue(value) {
    let decodedValue = value || '';
    const textarea = document.createElement('textarea');

    for (let index = 0; index < 2; index += 1) {
      textarea.innerHTML = decodedValue;
      const nextValue = textarea.value;

      if (nextValue === decodedValue) break;
      decodedValue = nextValue;
    }

    return decodedValue;
  }

  getDisplayTermForInternalQuery(value) {
    const cleanValue = this.decodeSearchDisplayValue(value).trim();
    const vendorMatch = cleanValue.match(/^vendor:\s*(?:"([^"]+)"|'([^']+)'|(.+))$/i);
    const skuMatch = cleanValue.match(/^variants\.sku:\s*(?:"((?:\\.|[^"])*)"|'((?:\\.|[^'])*)'|(.+))$/i);
    const exactPhraseMatch = cleanValue.match(/^"((?:\\.|[^"])*)"$/) || cleanValue.match(/^'((?:\\.|[^'])*)'$/);
    const vehiclePhraseWithTermsMatch = cleanValue.match(/^"((?:\\.|[^"])*)"\s+(.+)$/) || cleanValue.match(/^'((?:\\.|[^'])*)'\s+(.+)$/);
    const matchedTerm = skuMatch || vendorMatch || exactPhraseMatch || vehiclePhraseWithTermsMatch;

    if (!matchedTerm) return '';

    const displayTerm = vehiclePhraseWithTermsMatch && matchedTerm === vehiclePhraseWithTermsMatch
      ? `${vehiclePhraseWithTermsMatch[1]} ${vehiclePhraseWithTermsMatch[2]}`
      : (matchedTerm[1] || matchedTerm[2] || matchedTerm[3] || '');

    return this.decodeSearchDisplayValue(displayTerm)
      .replace(/\\(["\\])/g, '$1')
      .trim();
  }

  isLikelySku(value) {
    return /^[A-Za-z0-9][A-Za-z0-9._-]{4,}$/.test(value) && (/[-_.]/.test(value) || /^\d{5,}$/.test(value));
  }

  escapeSearchValue(value) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  normalizeSkuSearchTerm(value) {
    const cleanValue = (value || '').trim();

    if (/^variants\.sku:/i.test(cleanValue) || !this.isLikelySku(cleanValue)) {
      return value;
    }

    return `variants.sku:"${this.escapeSearchValue(cleanValue)}"`;
  }

  normalizeKnownStableSearchTerm(value) {
    const cleanValue = (value || '').trim().replace(/\s+/g, ' ');

    if (/^ap gewindefahrwerk$/i.test(cleanValue)) {
      return 'AP Gewindefahrwerke';
    }

    return value;
  }

  stripDiacritics(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  getVehicleMakeAliases() {
    return [
      'alfa romeo',
      'aston martin',
      'land rover',
      'mercedes benz',
      'mercedes-benz',
      'vw',
      'volkswagen',
      'audi',
      'bmw',
      'cupra',
      'seat',
      'skoda',
      'ford',
      'opel',
      'porsche',
      'renault',
      'peugeot',
      'citroen',
      'toyota',
      'mazda',
      'mini',
      'nissan',
      'hyundai',
      'kia',
      'honda',
      'volvo',
      'fiat',
      'jeep',
      'subaru',
      'mitsubishi',
      'suzuki'
    ].sort((a, b) => b.length - a.length);
  }

  getVehicleQuerySplitIndex(tokens) {
    const productWords = new Set([
      'ap',
      'bastuck',
      'bull-x',
      'bullx',
      'downpipe',
      'distanzscheiben',
      'egox',
      'eibach',
      'endrohr',
      'endschalldampfer',
      'endschalldaempfer',
      'fahrwerk',
      'federn',
      'frontlippe',
      'gewindefahrwerk',
      'grill',
      'grille',
      'grills',
      'handr',
      'heckansatz',
      'heckdiffusor',
      'hr',
      'h&r',
      'kw',
      'kuhlergrill',
      'kuehlergrill',
      'lowtec',
      'maxton',
      'milltek',
      'mts',
      'niere',
      'nieren',
      'nierengrill',
      'remus',
      'seitenschweller',
      'spoiler',
      'sportauspuff',
      'sportfahrwerk',
      'sportgrill',
      'spurverbreiterung',
      'spurverbreiterungen',
      'tieflegung',
      'tiefelegung',
      'tiefelegungsfedern',
      'tieferlegungsfedern'
    ]);

    for (let index = 0; index < tokens.length; index += 1) {
      if (productWords.has(tokens[index])) return index;
    }

    return -1;
  }

  normalizeVehicleSearchTerm(value) {
    const cleanValue = this.decodeSearchDisplayValue(value).trim().replace(/\s+/g, ' ');

    if (
      !cleanValue ||
      /^["'].*["']$/.test(cleanValue) ||
      /^(vendor|variants\.sku|tag):/i.test(cleanValue)
    ) {
      return value;
    }

    const lowerValue = this.stripDiacritics(cleanValue).toLowerCase().replace(/\s+/g, ' ');
    const makeAlias = this.getVehicleMakeAliases().find((alias) => (
      lowerValue === alias || lowerValue.startsWith(`${alias} `)
    ));

    if (!makeAlias || lowerValue === makeAlias) return value;

    const makeTokenCount = makeAlias.split(/\s+/).length;
    const originalTokens = cleanValue.split(/\s+/);
    const normalizedTokens = this.stripDiacritics(cleanValue)
      .toLowerCase()
      .replace(/&/g, 'and')
      .split(/\s+/);
    const remainderTokens = originalTokens.slice(makeTokenCount);

    if (!remainderTokens.length) return value;

    const splitIndex = this.getVehicleQuerySplitIndex(normalizedTokens.slice(makeTokenCount));
    const vehicleTokenCount = splitIndex === -1 ? remainderTokens.length : splitIndex;

    if (vehicleTokenCount < 1 || vehicleTokenCount > 4) return value;

    const vehiclePhrase = originalTokens.slice(0, makeTokenCount + vehicleTokenCount).join(' ');
    const trailingTerms = originalTokens.slice(makeTokenCount + vehicleTokenCount).join(' ');
    const quotedVehiclePhrase = `"${this.escapeSearchValue(vehiclePhrase)}"`;

    return trailingTerms ? `${quotedVehiclePhrase} ${trailingTerms}` : quotedVehiclePhrase;
  }

  normalizeSearchTerm(value) {
    const stableValue = this.normalizeKnownStableSearchTerm(value);

    if (stableValue !== value) {
      return stableValue;
    }

    const brandValue = this.normalizeBrandSearchTerm(value);

    if (brandValue !== value) {
      return brandValue;
    }

    const skuValue = this.normalizeSkuSearchTerm(value);

    if (skuValue !== value) {
      return skuValue;
    }

    return this.normalizeVehicleSearchTerm(value);
  }

  ensureHiddenInput(name, value) {
    const form = this.input?.form;
    if (!form) return;

    let field = Array.from(form.elements).find((element) => element.name === name);

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      form.appendChild(field);
    }

    field.value = value;
  }

  ensureHiddenQueryInput(value) {
    const form = this.input?.form;
    if (!form) return;

    let field = form.querySelector('input[type="hidden"][data-normalized-search-query="true"]');

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'q';
      field.dataset.normalizedSearchQuery = 'true';
      form.appendChild(field);
    }

    field.value = value;
  }

  ensureHiddenDisplayInput(value) {
    const form = this.input?.form;
    if (!form) return;

    let field = form.querySelector('input[type="hidden"][data-search-display-query="true"]');

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'bt_display_q';
      field.dataset.searchDisplayQuery = 'true';
      form.appendChild(field);
    }

    field.value = value;
  }

  clearHiddenQueryInput() {
    this.input?.form?.querySelector('input[type="hidden"][data-normalized-search-query="true"]')?.remove();
  }

  clearHiddenDisplayInput() {
    this.input?.form?.querySelector('input[type="hidden"][data-search-display-query="true"]')?.remove();
  }

  restoreVisibleQueryName() {
    if (this.input?.dataset.searchOriginalName && !this.input.getAttribute('name')) {
      this.input.setAttribute('name', this.input.dataset.searchOriginalName);
    }
  }

  suspendVisibleQueryName() {
    if (!this.input) return;

    const originalName = this.input.getAttribute('name');
    if (!originalName) return;

    this.input.dataset.searchOriginalName = originalName;
    this.input.removeAttribute('name');

    window.setTimeout(() => {
      this.restoreVisibleQueryName();
    }, 1000);
  }

  onFormSubmit() {
    this.restoreVisibleQueryName();
    this.clearHiddenQueryInput();
    this.clearHiddenDisplayInput();

    const originalValue = this.input.value;
    const normalizedValue = this.normalizeSearchTerm(originalValue);

    if (normalizedValue !== originalValue) {
      this.ensureHiddenQueryInput(normalizedValue);
      this.ensureHiddenDisplayInput(originalValue);
      this.suspendVisibleQueryName();
    }

    this.ensureHiddenInput('type', 'product');
    this.ensureHiddenInput('options[prefix]', 'last');
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a')
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      window.BestTuningSearchDisplayCleared = true;
      this.clearHiddenQueryInput();
      this.restoreVisibleQueryName();
      this.input.value = '';
      this.input.defaultValue = '';
      this.input.setAttribute('value', '');
      this.input.focus();
      this.toggleResetButton();
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

customElements.define('search-form', SearchForm);

(() => {
  const normalizer = document.createElement('search-form');

  const isSearchForm = (form) => {
    const action = form.getAttribute('action') || '';

    return action === '/search' ||
      action.endsWith('/search') ||
      action.includes('/search?') ||
      form.matches('[role="search"]');
  };

  const ensureHiddenField = (form, name, value) => {
    let field = Array.from(form.elements).find((element) => element.name === name);

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      form.appendChild(field);
    }

    field.value = value;
  };

  const removeSearchDisplayField = (form) => {
    Array.from(form.querySelectorAll('input[type="hidden"][name="bt_display_q"]')).forEach((field) => {
      field.remove();
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !isSearchForm(form)) return;
    if (form.closest('search-form, predictive-search, main-search')) return;

    const input = form.querySelector('input[type="search"][name="q"], input[name="q"]:not([type="hidden"])');
    if (!input) return;

    const originalValue = input.value;
    const normalizedValue = normalizer.normalizeSearchTerm(originalValue);
    removeSearchDisplayField(form);

    if (normalizedValue !== originalValue) {
      input.dataset.searchOriginalName = input.getAttribute('name') || 'q';
      input.removeAttribute('name');
      ensureHiddenField(form, 'q', normalizedValue);
      ensureHiddenField(form, 'bt_display_q', originalValue);

      window.setTimeout(() => {
        if (input.dataset.searchOriginalName && !input.getAttribute('name')) {
          input.setAttribute('name', input.dataset.searchOriginalName);
        }
      }, 1000);
    }

    ensureHiddenField(form, 'type', 'product');
    ensureHiddenField(form, 'options[prefix]', 'last');
  }, true);
})();

(() => {
  if (window.location.pathname !== '/search') return;

  const params = new URLSearchParams(window.location.search);
  const query = params.get('q') || '';
  const displayQuery = params.get('bt_display_q') || '';
  const normalizer = document.createElement('search-form');
  const normalizedQuery = normalizer.normalizeSearchTerm(query);
  const hasPrefixLast = params.get('options[prefix]') === 'last';
  const shouldNormalizeQuery = normalizedQuery && normalizedQuery !== query;

  if (query.trim() && (shouldNormalizeQuery || !hasPrefixLast || params.get('type') !== 'product')) {
    if (shouldNormalizeQuery) {
      params.set('q', normalizedQuery);
      params.set('bt_display_q', query.trim());
      params.delete('page');
    }

    params.set('type', 'product');
    params.set('options[prefix]', 'last');

    const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.location.replace(nextUrl);
      return;
    }
  }

  const decodeDisplayTerm = (value) => {
    let decodedValue = value || '';
    const textarea = document.createElement('textarea');

    for (let index = 0; index < 2; index += 1) {
      textarea.innerHTML = decodedValue;
      const nextValue = textarea.value;

      if (nextValue === decodedValue) break;
      decodedValue = nextValue;
    }

    return decodedValue;
  };
  const vendorMatch = query.match(/^vendor:\s*(?:"([^"]+)"|'([^']+)'|(.+))$/i);
  const skuMatch = query.match(/^variants\.sku:\s*(?:"((?:\\.|[^"])*)"|'((?:\\.|[^'])*)'|(.+))$/i);
  const exactPhraseMatch = query.match(/^"((?:\\.|[^"])*)"$/) || query.match(/^'((?:\\.|[^'])*)'$/);
  const vehiclePhraseWithTermsMatch = query.match(/^"((?:\\.|[^"])*)"\s+(.+)$/) || query.match(/^'((?:\\.|[^'])*)'\s+(.+)$/);
  const matchedTerm = skuMatch || vendorMatch || exactPhraseMatch || vehiclePhraseWithTermsMatch;
  const matchedDisplayTerm = vehiclePhraseWithTermsMatch && matchedTerm === vehiclePhraseWithTermsMatch
    ? `${vehiclePhraseWithTermsMatch[1]} ${vehiclePhraseWithTermsMatch[2]}`
    : (matchedTerm ? (matchedTerm[1] || matchedTerm[2] || matchedTerm[3] || '') : '');
  const displayTerm = displayQuery.trim()
    ? decodeDisplayTerm(displayQuery).trim()
    : (matchedTerm ? decodeDisplayTerm(matchedDisplayTerm.replace(/\\(["\\])/g, '$1')).trim() : '');

  if (!displayTerm) return;

  const getSearchDisplayInputs = () => Array.from(
    document.querySelectorAll('input[type="search"], input[name="q"]:not([type="hidden"]), .search__input, .search-input')
  ).filter((input) => input.tagName === 'INPUT' && input.type !== 'hidden');

  const updateSearchInputs = () => {
    if (window.BestTuningSearchDisplayCleared) return;

    getSearchDisplayInputs().forEach((input) => {
      input.value = displayTerm;
      input.defaultValue = displayTerm;
      input.setAttribute('value', displayTerm);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateSearchInputs);
  } else {
    updateSearchInputs();
  }

  window.addEventListener('pageshow', updateSearchInputs);
  window.setTimeout(updateSearchInputs, 100);
  window.setTimeout(updateSearchInputs, 500);
  window.setTimeout(updateSearchInputs, 1500);
})();
